import {
  AlertCircle,
  Calendar,
  Check,
  Clock,
  FileText,
  IndianRupee,
  MapPin,
  ShoppingCart,
  Star,
  User,
  Users,
  X,
} from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';

export const ViewTourModal = (props) => {
  const { isViewModalOpen, setIsViewModalOpen, tour } = props;
  if (!isViewModalOpen || !tour) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
      case 'rejected':
        return 'bg-red-500/10 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <MapPin size={20} className='text-primary' />
            </div>
            <h2 className='text-lg font-semibold'>Tour Package Details</h2>
          </div>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='p-1 hover:bg-muted rounded-md transition-colors'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 space-y-6'>
          {/* Images Section */}
          {tour.images && tour.images.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Tour Images
              </label>
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                {tour.images.map((image, index) => (
                  <div key={index} className='relative'>
                    <img
                      src={image?.url || image}
                      alt={image?.caption || `Tour image ${index + 1}`}
                      className='w-full h-32 object-cover rounded-lg border border-border'
                    />
                    {image?.isPrimary && (
                      <span className='absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded'>
                        Primary
                      </span>
                    )}
                    {image?.caption && (
                      <div className='absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg'>
                        {image.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {tour.videos && tour.videos.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Tour Videos
              </label>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {tour.videos.map((video, index) => (
                  <video
                    key={index}
                    src={video}
                    controls
                    className='w-full h-48 rounded-lg border border-border'
                  />
                ))}
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <FileText size={14} />
                <span>Tour Title</span>
              </label>
              <p className='text-sm font-medium'>{tour.title || 'N/A'}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Slug
              </label>
              <p className='text-sm text-muted-foreground'>
                {tour.slug || 'N/A'}
              </p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <MapPin size={14} />
                <span>Destination</span>
              </label>
              <p className='text-sm font-medium'>{tour?.destination || 'N/A'}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Tour Type
              </label>
              <p className='text-sm capitalize'>{tour?.tourType || 'N/A'}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Category
              </label>
              <p className='text-sm capitalize'>{tour?.subcategoryId?.name?.english || 'N/A'}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Clock size={14} />
                <span>Duration</span>
              </label>
              <p className='text-sm font-medium'>
                {tour.duration} {tour.duration === 1 ? 'day' : 'days'}
              </p>
            </div>
          </div>

          {/* Description */}
          {tour.description && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Description
              </label>
              <div className='bg-muted rounded-lg p-4'>
                <p className='text-sm'>{tour.description}</p>
              </div>
            </div>
          )}

          {/* Short Description */}
          {tour.shortDescription && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Short Description
              </label>
              <div className='bg-muted rounded-lg p-4'>
                <p className='text-sm'>{tour.shortDescription}</p>
              </div>
            </div>
          )}

          {/* Seller Info */}
          <div className='bg-muted rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <User size={14} />
              <span>Seller Information</span>
            </label>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Name:</p>
                <p className='text-sm font-medium'>
                  {tour.sellerId?.firstName || tour.sellerId?.lastName
                    ? `${tour.sellerId?.firstName || ''} ${tour.sellerId?.lastName || ''}`.trim()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Email:</p>
                <p className='text-sm'>{tour.sellerId?.email || 'N/A'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Phone:</p>
                <p className='text-sm'>{tour.sellerId?.phoneNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Itinerary */}
          {tour.itinerary && tour.itinerary.length > 0 && (
            <div className='space-y-3'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Itinerary
              </label>
              <div className='space-y-3'>
                {tour.itinerary.map((item, index) => (
                  <div
                    key={index}
                    className='bg-blue-500/5 border border-blue-500/20 rounded-lg p-4'
                  >
                    <div className='flex items-start justify-between mb-2'>
                      <div>
                        <span className='px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium'>
                          Day {item.day}
                        </span>
                        <h4 className='font-medium mt-2'>{item.title}</h4>
                      </div>
                    </div>
                    <p className='text-sm text-muted-foreground mb-3'>
                      {item.description}
                    </p>

                    {/* Activities */}
                    {item.activities && item.activities.length > 0 && (
                      <div className='mb-3'>
                        <p className='text-xs font-medium text-muted-foreground mb-2'>
                          Activities:
                        </p>
                        <div className='flex flex-wrap gap-2'>
                          {item.activities.map((activity, actIdx) => (
                            <span
                              key={actIdx}
                              className='px-2 py-1 bg-card border border-border rounded text-xs'
                            >
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Meals */}
                    {item.meals && (
                      <div className='flex gap-3 text-xs'>
                        {item.meals.breakfast && (
                          <span className='flex items-center gap-1'>
                            <Check size={12} className='text-green-500' />
                            Breakfast
                          </span>
                        )}
                        {item.meals.lunch && (
                          <span className='flex items-center gap-1'>
                            <Check size={12} className='text-green-500' />
                            Lunch
                          </span>
                        )}
                        {item.meals.dinner && (
                          <span className='flex items-center gap-1'>
                            <Check size={12} className='text-green-500' />
                            Dinner
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Info */}
          <div className='bg-green-500/5 border border-green-500/20 rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <IndianRupee size={14} />
              <span>Pricing Details</span>
            </label>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Base Price:
                </p>
                <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                  {tour.pricing?.currency || 'INR'}{' '}
                  {tour.pricing?.basePrice?.toFixed(2) || '0.00'}
                </p>
              </div>
              {tour.pricing?.discountedPrice && (
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Discounted Price:
                  </p>
                  <p className='text-sm font-medium'>
                    {tour.pricing?.currency || 'INR'}{' '}
                    {tour.pricing?.discountedPrice?.toFixed(2)}
                  </p>
                </div>
              )}
              <div>
                <p className='text-xs text-muted-foreground mb-1'>
                  Discount %:
                </p>
                <p className='text-sm font-medium'>
                  {tour.pricing?.discountPercentage > 0 ? (
                    <span className='px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-xs font-medium'>
                      {tour.pricing?.discountPercentage}% OFF
                    </span>
                  ) : (
                    '0%'
                  )}
                </p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Price Per:</p>
                <p className='text-sm font-medium'>
                  {tour.pricing?.pricePerPerson ? 'Person' : 'Group'}
                </p>
              </div>
            </div>
          </div>

          {/* Inclusions & Exclusions */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Inclusions */}
            <div className='bg-green-500/5 border border-green-500/20 rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase mb-3 block'>
                Inclusions
              </label>
              <div className='space-y-2'>
                {tour.inclusions?.accommodation && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Check size={14} className='text-green-500' />
                    Accommodation
                  </div>
                )}
                {tour.inclusions?.meals && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Check size={14} className='text-green-500' />
                    Meals
                  </div>
                )}
                {tour.inclusions?.transportation && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Check size={14} className='text-green-500' />
                    Transportation
                  </div>
                )}
                {tour.inclusions?.guide && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Check size={14} className='text-green-500' />
                    Guide
                  </div>
                )}
                {tour.inclusions?.entryFees && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Check size={14} className='text-green-500' />
                    Entry Fees
                  </div>
                )}
                {tour.inclusions?.other &&
                  tour.inclusions.other.map((item, idx) => (
                    <div key={idx} className='flex items-center gap-2 text-sm'>
                      <Check size={14} className='text-green-500' />
                      {item}
                    </div>
                  ))}
              </div>
            </div>

            {/* Exclusions */}
            <div className='bg-red-500/5 border border-red-500/20 rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase mb-3 block'>
                Exclusions
              </label>
              <div className='space-y-2'>
                {tour.exclusions?.flights && (
                  <div className='flex items-center gap-2 text-sm'>
                    <X size={14} className='text-red-500' />
                    Flights
                  </div>
                )}
                {tour.exclusions?.personalExpenses && (
                  <div className='flex items-center gap-2 text-sm'>
                    <X size={14} className='text-red-500' />
                    Personal Expenses
                  </div>
                )}
                {tour.exclusions?.insurance && (
                  <div className='flex items-center gap-2 text-sm'>
                    <X size={14} className='text-red-500' />
                    Insurance
                  </div>
                )}
                {tour.exclusions?.tips && (
                  <div className='flex items-center gap-2 text-sm'>
                    <X size={14} className='text-red-500' />
                    Tips
                  </div>
                )}
                {tour.exclusions?.other &&
                  tour.exclusions.other.map((item, idx) => (
                    <div key={idx} className='flex items-center gap-2 text-sm'>
                      <X size={14} className='text-red-500' />
                      {item}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Location Details */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {tour.pickupLocation && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase'>
                  Pickup Location
                </label>
                <p className='text-sm'>{tour.pickupLocation}</p>
              </div>
            )}
            {tour.dropLocation && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase'>
                  Drop Location
                </label>
                <p className='text-sm'>{tour.dropLocation}</p>
              </div>
            )}
          </div>

          {/* Group Size */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {tour.minGroupSize && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center gap-1'>
                  <Users size={14} />
                  Min Group Size
                </label>
                <p className='text-sm font-medium'>{tour.minGroupSize}</p>
              </div>
            )}
            {tour.maxGroupSize && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center gap-1'>
                  <Users size={14} />
                  Max Group Size
                </label>
                <p className='text-sm font-medium'>{tour.maxGroupSize}</p>
              </div>
            )}
            {tour.ageRestriction?.minAge && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase'>
                  Min Age
                </label>
                <p className='text-sm'>{tour.ageRestriction.minAge} years</p>
              </div>
            )}
            {tour.ageRestriction?.maxAge && (
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase'>
                  Max Age
                </label>
                <p className='text-sm'>{tour.ageRestriction.maxAge} years</p>
              </div>
            )}
          </div>

          {/* Things to Carry */}
          {tour.thingsToCarry && tour.thingsToCarry.length > 0 && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Things to Carry
              </label>
              <div className='flex flex-wrap gap-2'>
                {tour.thingsToCarry.map((item, idx) => (
                  <span
                    key={idx}
                    className='px-3 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-xs'
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Cancellation Policy */}
          {tour.cancellationPolicy && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-muted-foreground uppercase'>
                Cancellation Policy
              </label>
              <div className='bg-muted rounded-lg p-4'>
                <p className='text-sm'>{tour.cancellationPolicy}</p>
              </div>
            </div>
          )}

          {/* Approval Status */}
          <div className='bg-muted rounded-lg p-4 space-y-3'>
            <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
              <AlertCircle size={14} />
              <span>Approval Information</span>
            </label>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
              <div>
                <p className='text-xs text-muted-foreground mb-1'>Status:</p>
                <span
                  className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(tour.approvalStatus)}`}
                >
                  {tour.approvalStatus}
                </span>
              </div>
              {/* {tour.approvedBy && (
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Approved By:
                  </p>
                  <p className='text-sm'>
                    {tour.approvedBy?.firstName || tour.approvedBy?.lastName
                      ? `${tour.approvedBy?.firstName || ''} ${tour.approvedBy?.lastName || ''}`.trim()
                      : 'N/A'}
                  </p>
                </div>
              )} */}
              {tour.approvedAt && (
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Approved At:
                  </p>
                  <p className='text-sm'>{formatDateTime(tour.approvedAt)}</p>
                </div>
              )}
            </div>
            {tour.rejectionReason && (
              <div className='bg-red-500/10 border border-red-500/20 rounded p-3'>
                <p className='text-xs text-muted-foreground mb-1'>
                  Rejection Reason:
                </p>
                <p className='text-sm text-red-600 dark:text-red-400'>
                  {tour.rejectionReason}
                </p>
              </div>
            )}
          </div>

          {/* Status Badges */}
          {/* <div className='space-y-2'>
            <label className='text-xs font-medium text-muted-foreground uppercase'>
              Status & Flags
            </label>
            <div className='flex flex-wrap gap-2'>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  tour.isActive
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {tour.isActive ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  tour.isPopular
                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {tour.isPopular ? 'Popular' : 'Regular'}
              </span>
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  tour.isFeatured
                    ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                    : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                }`}
              >
                {tour.isFeatured ? 'Featured' : 'Normal'}
              </span>
            </div>
          </div> */}

          {/* Statistics */}
          {/* {tour.stats && (
            <div className='bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 space-y-3'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <ShoppingCart size={14} />
                <span>Booking Statistics</span>
              </label>
              <div className='grid grid-cols-3 gap-4'>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Total Bookings:
                  </p>
                  <p className='text-lg font-bold'>
                    {tour.stats.totalBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Completed:
                  </p>
                  <p className='text-lg font-bold text-green-600 dark:text-green-400'>
                    {tour.stats.completedBookings || 0}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-muted-foreground mb-1'>
                    Cancelled:
                  </p>
                  <p className='text-lg font-bold text-red-600 dark:text-red-400'>
                    {tour.stats.cancelledBookings || 0}
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Ratings */}
          {/* {tour.ratings && (
            <div className='bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <Star
                    size={16}
                    className='text-yellow-600 dark:text-yellow-400'
                  />
                  <span className='text-xs font-medium text-muted-foreground uppercase'>
                    Ratings
                  </span>
                </div>
                <div className='text-right'>
                  <p className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                    {tour.ratings.averageRating?.toFixed(1) || '0.0'}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {tour.ratings.totalRatings || 0} ratings
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Timestamps */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Created At</span>
              </label>
              <p className='text-sm'>{formatDateTime(tour.createdAt)}</p>
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Calendar size={14} />
                <span>Updated At</span>
              </label>
              <p className='text-sm'>{formatDateTime(tour.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end px-6 py-4 border-t border-border'>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
